/**
 * Backing PQI Redux slice — completely independent from existing PQI module.
 * State: sets, activeTab, cycleNumber, headerValues, rows, attachments, offlineQueue, syncStatus.
 * Manages multi-product, multi-set per cycle, header-per-set, offline queue, attachment tracking, sync status.
 */

import { createSlice } from "@reduxjs/toolkit";

const TAB_KEYS = ["Creates", "Packs", "Product", "Net Weight"];
const TOTAL_CYCLES = 8;

const initialSets = () => {
  const s = {};
  TAB_KEYS.forEach((tab) => {
    s[tab] = {};
    for (let c = 1; c <= TOTAL_CYCLES; c++) s[tab][c] = 0;
  });
  return s;
};

const initialState = {
  sets: initialSets(),
  activeTab: "Creates",
  cycleNumber: 1,
  headerValues: {}, // { [tab]: { [cycleNo]: { [setIndex]: { product, batchNo, lineNo, packaged, expiry, executiveName, standardWeight } } } }
  rows: {}, // { [tab]: { [cycleNo]: { [setIndex]: [ { itemNum, status, defectCategory, defect, majorDefect, remarks, netWeight } ] } } }
  attachments: {}, // { [key]: [] } key = tab_cycle_setIndex_itemNum or recordId
  offlineQueue: [], // { id, category, cycleNo, records, timestamp }[]
  syncStatus: "idle", // idle | syncing | success | failed
  offlineSubmissionsByCategory: {}, // { [category]: [ { cycleNo, records } ] } for sync compatibility
};

const backingPQISlice = createSlice({
  name: "backingPQI",
  initialState,
  reducers: {
    setActiveTab: (state, action) => {
      const tab = action.payload;
      if (TAB_KEYS.includes(tab)) state.activeTab = tab;
    },
    setCycleNumber: (state, action) => {
      const n = Number(action.payload);
      if (n >= 1 && n <= TOTAL_CYCLES) state.cycleNumber = n;
    },
    setHeaderValues: (state, action) => {
      const { tab, cycleNo, setIndex, values } = action.payload;
      if (!state.headerValues[tab]) state.headerValues[tab] = {};
      if (!state.headerValues[tab][cycleNo]) state.headerValues[tab][cycleNo] = {};
      state.headerValues[tab][cycleNo][setIndex] = values;
    },
    setRows: (state, action) => {
      const { tab, cycleNo, setIndex, rows: rowList } = action.payload;
      if (!state.rows[tab]) state.rows[tab] = {};
      if (!state.rows[tab][cycleNo]) state.rows[tab][cycleNo] = {};
      state.rows[tab][cycleNo][setIndex] = rowList;
    },
    addSet: (state, action) => {
      const { tab, cycleNo } = action.payload;
      if (!state.sets[tab]) state.sets[tab] = {};
      const current = state.sets[tab][cycleNo] ?? 0;
      state.sets[tab][cycleNo] = current + 1;
    },
    setSetCount: (state, action) => {
      const { tab, cycleNo, count } = action.payload;
      if (state.sets[tab]) state.sets[tab][cycleNo] = Math.max(0, count);
    },
    addAttachment: (state, action) => {
      const { key, file } = action.payload;
      if (!state.attachments[key]) state.attachments[key] = [];
      state.attachments[key].push(file);
    },
    removeAttachment: (state, action) => {
      const { key, index } = action.payload;
      if (state.attachments[key]) {
        state.attachments[key].splice(index, 1);
        if (state.attachments[key].length === 0) delete state.attachments[key];
      }
    },
    clearAttachmentsForKey: (state, action) => {
      delete state.attachments[action.payload];
    },
    enqueueOffline: (state, action) => {
      const { id, category, cycleNo, records } = action.payload;
      state.offlineQueue.push({
        id: id || `bpqi_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        category,
        cycleNo,
        records,
        timestamp: Date.now(),
      });
      // Keep offlineSubmissionsByCategory in sync for HomePage sync logic
      if (!state.offlineSubmissionsByCategory[category]) state.offlineSubmissionsByCategory[category] = [];
      state.offlineSubmissionsByCategory[category].push({ cycleNo, records });
    },
    removeFromOfflineQueue: (state, action) => {
      const id = action.payload;
      state.offlineQueue = state.offlineQueue.filter((item) => item.id !== id);
      // Rebuild offlineSubmissionsByCategory from remaining queue
      state.offlineSubmissionsByCategory = {};
      state.offlineQueue.forEach((item) => {
        if (!state.offlineSubmissionsByCategory[item.category]) state.offlineSubmissionsByCategory[item.category] = [];
        state.offlineSubmissionsByCategory[item.category].push({ cycleNo: item.cycleNo, records: item.records });
      });
    },
    setSyncStatus: (state, action) => {
      const status = action.payload;
      if (["idle", "syncing", "success", "failed"].includes(status)) state.syncStatus = status;
    },
    clearOfflineQueue: (state) => {
      state.offlineQueue = [];
      state.offlineSubmissionsByCategory = {};
    },
    setOfflineSubmissionsByCategory: (state, action) => {
      state.offlineSubmissionsByCategory = action.payload || {};
    },
    resetState: () => initialState,
  },
});

export const {
  setActiveTab,
  setCycleNumber,
  setHeaderValues,
  setRows,
  addSet,
  setSetCount,
  addAttachment,
  removeAttachment,
  clearAttachmentsForKey,
  enqueueOffline,
  removeFromOfflineQueue,
  setSyncStatus,
  clearOfflineQueue,
  setOfflineSubmissionsByCategory,
  resetState,
} = backingPQISlice.actions;

export default backingPQISlice.reducer;
